import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { defaultReportData } from '../utils/defaultData';

const { 
  FiDownload, 
  FiCheck, 
  FiX, 
  FiFilter, 
  FiLayout, 
  FiCalendar, 
  FiStar, 
  FiTrendingUp,
  FiFileText,
  FiSmartphone,
  FiYoutube, // Added for Video section
  FiVideo    // Fallback
} = FiIcons;

const ReportGenerator = ({ selectedClient }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState(defaultReportData);
  const [selectedSections, setSelectedSections] = useState({});
  const reportRef = useRef(null);

  useEffect(() => {
    if (selectedClient) {
      const savedData = localStorage.getItem(`ar_data_${selectedClient.id}`);
      const data = savedData ? JSON.parse(savedData) : defaultReportData;
      setReportData(data);

      const initialSections = {
        overview: true,
        seo: hasData(data.seo, 'seo'),
        gbp: hasData(data.gbp, 'gbp'),
        social: hasData(data.social, 'social'),
        googleAds: hasData(data.googleAds, 'ads'),
        metaAds: hasData(data.metaAds, 'ads'),
        youtube: hasData(data.youtube, 'youtube'),
        email: hasData(data.email, 'email'),
        content: hasData(data.content, 'content'),
      };
      setSelectedSections(initialSections);
    }
  }, [selectedClient]);

  const hasData = (sectionData, type) => {
    if (!sectionData) return false;
    switch (type) {
      case 'seo': return sectionData.traffic > 0 || sectionData.keywords?.length > 0;
      case 'gbp': return sectionData.actions > 0 || sectionData.views > 0;
      case 'social': 
        return sectionData.platforms && Object.values(sectionData.platforms).some(p => p.impressions > 0 || p.followers > 0);
      case 'ads': return sectionData.spend > 0 || sectionData.campaigns?.length > 0 || sectionData.adSets?.length > 0;
      case 'youtube': return sectionData.views > 0;
      case 'email': return sectionData.emailsSent > 0 || sectionData.listSize > 0;
      case 'content': return sectionData.blogPostsCount > 0 || sectionData.upcomingBlogs?.length > 0 || sectionData.upcomingVideos?.length > 0;
      default: return false;
    }
  };

  const toggleSection = (key) => {
    setSelectedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const generatePDF = async () => {
    if (!selectedClient) return;
    setIsGenerating(true);

    try {
      const reportElement = reportRef.current;
      const pages = reportElement.querySelectorAll('.report-page');
      
      // A4 dimensions in mm
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        
        // Prepare page for capture to avoid layout shifts
        page.style.boxShadow = 'none';
        page.style.margin = '0';
        // Force text rendering for better clarity
        page.style.fontFeatureSettings = '"kern" 1, "liga" 1';
        page.style.textRendering = 'optimizeLegibility'; 

        const canvas = await html2canvas(page, {
          scale: 3, // Increased scale for sharper text
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 1200, // Force desktop width to prevent responsive shifts
          onclone: (clonedDoc) => {
             // Ensure cloned styles are print-friendly
             const clonedPage = clonedDoc.querySelector('.report-page');
             if (clonedPage) {
                 clonedPage.style.transform = 'none';
                 clonedPage.style.letterSpacing = 'normal'; // Fix text spacing issues
             }
          }
        });

        // Restore original styles
        page.style.boxShadow = '';
        page.style.margin = '';
        page.style.fontFeatureSettings = '';
        page.style.textRendering = '';

        const imgData = canvas.toDataURL('image/jpeg', 1.0); // Use JPEG with max quality
        
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }

      pdf.save(`${selectedClient.name.replace(/\s+/g, '_')}_Report.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const getFormattedDate = () => {
      if (reportData.overview.reportDate) {
          const date = new Date(reportData.overview.reportDate);
          // Add timezone offset correction to prevent "day before" bug
          const userTimezoneOffset = date.getTimezoneOffset() * 60000;
          const adjustedDate = new Date(date.getTime() + userTimezoneOffset);
          return adjustedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      }
      return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (!selectedClient) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400">
        <SafeIcon icon={FiLayout} className="text-6xl mb-4 opacity-20" />
        <p className="text-lg">Select a client to generate a report</p>
      </div>
    );
  }

  const sectionConfig = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'seo', label: 'SEO Performance' },
    { id: 'gbp', label: 'Google Business Profile' },
    { id: 'social', label: 'Social Media' },
    { id: 'googleAds', label: 'Google Ads' },
    { id: 'metaAds', label: 'Meta Ads' },
    { id: 'youtube', label: 'YouTube Ads' },
    { id: 'email', label: 'Email Marketing' },
    { id: 'content', label: 'Content Planning' },
  ];

  return (
    <div className="flex h-full bg-gray-100 overflow-hidden">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon icon={FiFilter} /> Report Configuration
          </h2>
          <p className="text-xs text-gray-500 mt-1">Select sections to include</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {sectionConfig.map(section => (
            <div 
              key={section.id}
              onClick={() => toggleSection(section.id)}
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                selectedSections[section.id] 
                  ? 'bg-green-50 border border-[#00A859] text-[#002B60]' 
                  : 'bg-gray-50 border border-gray-200 text-gray-400 hover:bg-gray-100'
              }`}
            >
              <span className="font-medium text-sm">{section.label}</span>
              {selectedSections[section.id] ? (
                <SafeIcon icon={FiCheck} className="text-[#00A859]" />
              ) : (
                <SafeIcon icon={FiX} className="text-gray-400" />
              )}
            </div>
          ))}
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={generatePDF}
            disabled={isGenerating}
            className="w-full py-3 px-4 bg-[#00A859] text-white rounded-lg font-bold shadow-md hover:bg-[#008a4a] transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-wait"
          >
            {isGenerating ? (
              <>Generating...</>
            ) : (
              <>
                <SafeIcon icon={FiDownload} /> Download Report
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview Area */}
      <div className="flex-1 overflow-y-auto bg-gray-200 p-8 flex justify-center">
        <div ref={reportRef} className="scale-90 origin-top">
          {/* Cover Page */}
          <ReportPage className="flex flex-col justify-between bg-gradient-to-br from-[#002B60] to-[#001f4d] text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#00A859] opacity-10 rounded-full translate-x-1/3 -translate-y-1/3"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00A859] opacity-10 rounded-full -translate-x-1/3 translate-y-1/3"></div>
            
            <div className="p-16 z-10">
              <div className="text-[#00A859] font-bold text-2xl tracking-widest mb-2">MONTHLY REPORT</div>
              <h1 className="text-6xl font-bold mb-6">{selectedClient.name}</h1>
              <div className="flex items-center gap-3 text-gray-300 text-xl">
                 <SafeIcon icon={FiCalendar} />
                 <span>{getFormattedDate()}</span>
              </div>
            </div>

            <div className="p-16 z-10">
              <div className="w-24 h-1 bg-[#00A859] mb-8"></div>
              <div className="text-2xl font-light opacity-90">Prepared by</div>
              <div className="text-3xl font-bold mt-2">AlbertaRank</div>
              <div className="text-gray-400 mt-2">Advanced SEO & Marketing Analytics</div>
            </div>
          </ReportPage>

          {/* Overview Page */}
          {selectedSections.overview && (
            <ReportPage title="Executive Summary" sub="Key Performance Indicators">
              <div className="grid grid-cols-2 gap-6 mb-8">
                <MetricCard label="Total Traffic" value={reportData.overview.traffic.toLocaleString()} color="blue" />
                <MetricCard label="Conversions" value={reportData.overview.conversions.toLocaleString()} color="green" />
                <MetricCard label="Revenue" value={`$${reportData.overview.revenue.toLocaleString()}`} color="purple" />
                <MetricCard label="Tech Health" value={`${reportData.overview.techHealth}/100`} color="orange" />
              </div>
              
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100 min-h-[16rem]">
                 <h3 className="text-[#002B60] font-bold mb-4 text-xl">Monthly Analysis</h3>
                 <p className="text-gray-700 leading-relaxed whitespace-pre-wrap font-sans text-lg">
                    {reportData.overview.executiveSummary || "No summary provided for this month."}
                 </p>
              </div>
            </ReportPage>
          )}

          {/* SEO Page */}
          {selectedSections.seo && (
            <ReportPage title="Organic Search (SEO)" sub="Search Engine Performance">
              <div className="grid grid-cols-4 gap-4 mb-8">
                <MiniMetric label="Traffic" value={reportData.seo.traffic} />
                <MiniMetric label="Backlinks" value={reportData.seo.backlinks} />
                <MiniMetric label="Health" value={`${reportData.seo.techHealth}/100`} />
                <MiniMetric label="Conversions" value={reportData.seo.conversions} />
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Top Performing Keywords</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-left">
                    <th className="pb-2 pl-2">Keyword</th>
                    <th className="pb-2 text-center">Position</th>
                    <th className="pb-2 text-right">Volume</th>
                    <th className="pb-2 text-center">Diff</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData.seo.keywords.slice(0, 12).map((k, i) => (
                    <tr key={i}>
                      <td className="py-3 pl-2 font-medium text-gray-700">{k.keyword}</td>
                      <td className="py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${k.position <= 3 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {k.position}
                        </span>
                      </td>
                      <td className="py-3 text-right text-gray-600">{k.searches}</td>
                      <td className="py-3 text-center text-gray-500">{k.difficulty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!reportData.seo.keywords || reportData.seo.keywords.length === 0) && (
                <p className="text-center text-gray-400 py-8 italic">No keyword data recorded for this period.</p>
              )}
            </ReportPage>
          )}

          {/* Google Business Profile */}
          {selectedSections.gbp && (
            <ReportPage title="Google Business Profile" sub="Local Search Performance">
              <div className="grid grid-cols-2 gap-6 mb-8">
                <MetricCard label="Total Actions" value={reportData.gbp.actions} color="blue" />
                <MetricCard label="Profile Views" value={reportData.gbp.views} color="green" />
                <MetricCard label="Average Rating" value={reportData.gbp.rating} color="yellow" />
                <MetricCard label="New Reviews" value={reportData.gbp.newReviews} color="purple" />
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Recent Reviews</h3>
                <div className="space-y-4">
                  {reportData.gbp.reviews && reportData.gbp.reviews.slice(0, 3).map((review, i) => (
                    <div key={i} className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                       <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-gray-800">{review.name}</span>
                          <span className="text-xs text-gray-500">{review.date}</span>
                       </div>
                       <div className="flex text-yellow-400 mb-2">
                          {[...Array(5)].map((_, i) => (
                             <SafeIcon key={i} icon={FiStar} className={i < Math.floor(review.rating) ? "fill-current" : "text-gray-300"} />
                          ))}
                       </div>
                       <p className="text-sm text-gray-600 italic">"{review.text}"</p>
                    </div>
                  ))}
                  {(!reportData.gbp.reviews || reportData.gbp.reviews.length === 0) && (
                    <p className="text-gray-400 italic">No new reviews this month.</p>
                  )}
                </div>
              </div>
            </ReportPage>
          )}

          {/* Google Ads Page */}
          {selectedSections.googleAds && (
            <ReportPage title="Google Ads (PPC)" sub="Paid Search Performance">
               <div className="grid grid-cols-3 gap-6 mb-8">
                <MetricCard label="Total Spend" value={`$${reportData.googleAds.spend.toLocaleString()}`} color="blue" />
                <MetricCard label="ROAS" value={`${reportData.googleAds.roas}x`} color="green" />
                <MetricCard label="Conversions" value={reportData.googleAds.conversions} color="purple" />
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Campaign Breakdown</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-gray-500 text-left">
                    <th className="p-3 rounded-l-lg">Campaign</th>
                    <th className="p-3 text-right">Spend</th>
                    <th className="p-3 text-right">Conv.</th>
                    <th className="p-3 text-right rounded-r-lg">ROAS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData.googleAds.campaigns.map((c, i) => (
                    <tr key={i}>
                      <td className="p-3 font-medium text-gray-700">{c.name}</td>
                      <td className="p-3 text-right text-gray-600">${c.spend.toLocaleString()}</td>
                      <td className="p-3 text-right text-gray-600">{c.conversions}</td>
                      <td className="p-3 text-right font-bold text-green-600">{c.roas}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ReportPage>
          )}

           {/* Meta Ads Page */}
           {selectedSections.metaAds && (
            <ReportPage title="Meta Ads" sub="Facebook & Instagram Performance">
               <div className="grid grid-cols-3 gap-6 mb-8">
                <MetricCard label="ROAS" value={`${reportData.metaAds.roas}x`} color="blue" />
                <MetricCard label="CTR" value={`${reportData.metaAds.ctr}%`} color="green" />
                <MetricCard label="CPM" value={`$${reportData.metaAds.cpm}`} color="orange" />
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Ad Set Performance</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-gray-500 text-left">
                    <th className="p-3 rounded-l-lg">Ad Set</th>
                    <th className="p-3 text-center">Platform</th>
                    <th className="p-3 text-right">Spend</th>
                    <th className="p-3 text-right rounded-r-lg">ROAS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData.metaAds.adSets && reportData.metaAds.adSets.map((ad, i) => (
                    <tr key={i}>
                      <td className="p-3 font-medium text-gray-700">{ad.name}</td>
                      <td className="p-3 text-center text-gray-500">{ad.platform}</td>
                      <td className="p-3 text-right text-gray-600">${ad.spend?.toLocaleString()}</td>
                      <td className="p-3 text-right font-bold text-blue-600">{ad.roas}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ReportPage>
          )}

           {/* YouTube Ads Page */}
           {selectedSections.youtube && (
            <ReportPage title="YouTube Advertising" sub="Video Campaign Performance">
               <div className="grid grid-cols-3 gap-6 mb-8">
                <MetricCard label="Total Views" value={reportData.youtube.views.toLocaleString()} color="red" />
                <MetricCard label="View Rate" value={`${reportData.youtube.viewRate}%`} color="blue" />
                <MetricCard label="Cost / View" value={`$${reportData.youtube.costPerView}`} color="green" />
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Campaigns</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-gray-500 text-left">
                    <th className="p-3 rounded-l-lg">Campaign</th>
                    <th className="p-3 text-right">Views</th>
                    <th className="p-3 text-right rounded-r-lg">Completion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData.youtube.campaigns && reportData.youtube.campaigns.map((c, i) => (
                    <tr key={i}>
                      <td className="p-3 font-medium text-gray-700">{c.name}</td>
                      <td className="p-3 text-right text-gray-600">{c.views.toLocaleString()}</td>
                      <td className="p-3 text-right text-gray-600">{c.completion}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ReportPage>
          )}

           {/* Email Marketing Page */}
           {selectedSections.email && (
            <ReportPage title="Email Marketing" sub="Campaign Engagement">
              <div className="grid grid-cols-4 gap-4 mb-8">
                <MiniMetric label="List Size" value={reportData.email.listSize} />
                <MiniMetric label="Emails Sent" value={reportData.email.emailsSent} />
                <MiniMetric label="Open Rate" value={`${reportData.email.openRate}%`} />
                <MiniMetric label="Click Rate" value={`${reportData.email.clickRate}%`} />
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Recent Campaigns</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-gray-500 text-left">
                    <th className="p-3 rounded-l-lg">Campaign Name</th>
                    <th className="p-3 text-right">Sent</th>
                    <th className="p-3 text-right rounded-r-lg">Open Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData.email.campaigns && reportData.email.campaigns.map((c, i) => (
                    <tr key={i}>
                      <td className="p-3 font-medium text-gray-700">{c.name}</td>
                      <td className="p-3 text-right text-gray-600">{c.sent.toLocaleString()}</td>
                      <td className="p-3 text-right font-bold text-green-600">{c.openRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ReportPage>
          )}

           {/* Content Planning Page */}
           {selectedSections.content && (
            <ReportPage title="Content Planning" sub="Upcoming Schedule">
              <div className="grid grid-cols-2 gap-6 mb-8">
                 <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                    <h3 className="text-blue-800 font-bold mb-1">Blog Posts</h3>
                    <div className="text-4xl font-bold text-blue-600">{reportData.content.blogPostsCount}</div>
                    <div className="text-sm text-blue-500 mt-2">Scheduled or Drafted</div>
                 </div>
                 <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                    <h3 className="text-purple-800 font-bold mb-1">Social Posts</h3>
                    <div className="text-4xl font-bold text-purple-600">{reportData.content.socialPostsCount}</div>
                    <div className="text-sm text-purple-500 mt-2">Planned Content</div>
                 </div>
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Upcoming Content</h3>
              <div className="space-y-3">
                 {/* Blog Posts */}
                 {reportData.content.upcomingBlogs && reportData.content.upcomingBlogs.map((blog, i) => (
                    <div key={`blog-${i}`} className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-100">
                       <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center text-gray-300 border border-gray-200 mr-4">
                          <SafeIcon icon={FiFileText} size={24} />
                       </div>
                       <div className="flex-1">
                          <h4 className="font-bold text-gray-800">{blog.title}</h4>
                          <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                             <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs uppercase font-bold">{blog.status}</span>
                             <span>• Due: {blog.publishDate}</span>
                          </div>
                       </div>
                    </div>
                 ))}
                 
                 {/* Social Posts */}
                 {reportData.content.upcomingSocial && reportData.content.upcomingSocial.length > 0 && reportData.content.upcomingSocial.map((post, i) => (
                    <div key={`social-${i}`} className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center text-gray-300 border border-gray-200 mr-4">
                            <SafeIcon icon={FiSmartphone} size={24} />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-bold text-gray-800">{post.content.length > 40 ? post.content.substring(0, 40) + '...' : post.content}</h4>
                            <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs uppercase font-bold">{post.platform}</span>
                                <span>• Due: {post.publishDate}</span>
                            </div>
                        </div>
                    </div>
                 ))}

                 {/* Video Content - NEWLY ADDED */}
                 {reportData.content.upcomingVideos && reportData.content.upcomingVideos.length > 0 && reportData.content.upcomingVideos.map((video, i) => (
                    <div key={`video-${i}`} className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center text-gray-300 border border-gray-200 mr-4">
                            <SafeIcon icon={FiYoutube} size={24} />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-bold text-gray-800">{video.title}</h4>
                            <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                                <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs uppercase font-bold">{video.platform}</span>
                                <span>• Due: {video.publishDate}</span>
                            </div>
                        </div>
                    </div>
                 ))}
              </div>
            </ReportPage>
          )}

          {/* Social Media Page */}
          {selectedSections.social && (
             <ReportPage title="Social Media" sub="Platform Performance">
                <div className="grid grid-cols-1 gap-6">
                    {Object.entries(reportData.social.platforms).map(([platform, stats]) => (
                        (stats.impressions > 0 || stats.followers > 0) && (
                            <div key={platform} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center text-2xl uppercase font-bold text-gray-400">
                                        {platform.charAt(0)}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 capitalize">{platform}</h4>
                                        <div className="text-xs text-gray-500">{stats.posts} Posts Published</div>
                                    </div>
                                </div>
                                <div className="flex gap-8 text-center">
                                    <div>
                                        <div className="text-lg font-bold text-gray-800">{stats.impressions.toLocaleString()}</div>
                                        <div className="text-xs text-gray-500">Impressions</div>
                                    </div>
                                    <div>
                                        <div className="text-lg font-bold text-gray-800">{stats.engagement.toLocaleString()}</div>
                                        <div className="text-xs text-gray-500">Engagement</div>
                                    </div>
                                     <div>
                                        <div className="text-lg font-bold text-gray-800">{stats.followers.toLocaleString()}</div>
                                        <div className="text-xs text-gray-500">Followers</div>
                                    </div>
                                </div>
                            </div>
                        )
                    ))}
                </div>
             </ReportPage>
          )}

        </div>
      </div>
    </div>
  );
};

// --- Sub-components for modern layout ---

const ReportPage = ({ children, className = "", title, sub }) => (
  <div 
    className={`report-page bg-white shadow-xl mx-auto mb-8 overflow-hidden flex flex-col ${className}`}
    style={{ width: '210mm', height: '297mm', padding: className.includes('bg-') ? '0' : '20mm' }} // A4 Size
  >
    {title && (
      <div className="mb-10 border-b-2 border-[#00A859] pb-4">
        <h2 className="text-3xl font-bold text-[#002B60]">{title}</h2>
        {sub && <p className="text-gray-500 text-lg mt-1">{sub}</p>}
      </div>
    )}
    <div className="flex-1">
      {children}
    </div>
    {!className.includes('bg-') && (
      <div className="mt-auto pt-6 border-t border-gray-100 flex justify-between text-xs text-gray-400">
        <span>AlbertaRank Reporting</span>
        <span>{new Date().toLocaleDateString()}</span>
      </div>
    )}
  </div>
);

const MetricCard = ({ label, value, color }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    green: 'bg-green-50 text-green-700 border-green-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-100',
  };
  
  return (
    <div className={`p-6 rounded-xl border ${colors[color] || colors.blue}`}>
      <p className="text-sm opacity-80 mb-1">{label}</p>
      <p className="text-3xl font-bold">{value}</p>
    </div>
  );
};

const MiniMetric = ({ label, value }) => (
  <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
    <div className="text-xl font-bold text-gray-800">{value?.toLocaleString()}</div>
    <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">{label}</div>
  </div>
);

export default ReportGenerator;